import { mergeStyleSets, Text, TextField } from "@fluentui/react";

import { IconButton } from "@fluentui/react";
import { adjustDateValue, transformTaxonomyValue } from "form/util";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useMask from "react-mask-hook";
import { useSelector } from "store/hooks";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import * as API from "../../store/api/api";
import { FieldContainer } from "./FieldContainer";

const getClasses = (theme, isDarkMode, disabled) =>
  mergeStyleSets({
    label: {
      fontSize: "14px",
      fontWeight: "600",
      padding: "5px",
      display: "block",
      color: (function () {
        if (disabled) {
          return theme.palette.neutralSecondaryAlt;
        }
        if (isDarkMode) {
          return "white";
        }
        return theme.palette.black;
      })(),
    },
    labelAsterix: {
      color: theme.palette.redDark,
    },
  });

const getTextFieldStyle = (transparent, theme, isMarked, confidentiality) => ({
  root: {
    ".ms-TextField-fieldGroup": {
      borderTop: "none !important",
      borderRight: "none !important",
      borderLeft: "none !important",
      background: transparent ? "transparent" : "inherit",
      ...(isMarked && { background: theme.palette.yellowLight }),
      ...(confidentiality && {
        background: `rgb(${confidentiality < 0.9 ? 255 : 200},${
          confidentiality >= 0.8 ? 255 : 200
        },200)`,
      }),
    },
    ".ms-TextField-fieldGroup::after": {
      borderTop: "none !important",
      borderRight: "none !important",
      borderLeft: "none !important",
    },
    ".ms-TextField-fieldGroup input": {
      color: isMarked
        ? "black"
        : theme.isDark
        ? confidentiality
          ? "black"
          : "white"
        : "black",
    },

    ".ms-TextField-fieldGroup input:disabled": {
      background: transparent ? "transparent" : "inherit",
      color: theme.palette.neutralSecondaryAlt,
    },
    ".ms-TextField-fieldGroup textarea:disabled": {
      background: theme.palette.white,
    },
  },
});

type Props = {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name: string;
  disabled?: boolean;
  label: string;
  errors?: any;
  transparent?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  isMarked?: boolean;
  confidentiality?: number;
  setFieldValue: any;
  [x: string]: any; //rest props
};

const VinSearchTextInputField = ({
  disabled,
  label,
  name,
  onChange,
  error = "",
  value,
  errors,
  transparent,
  required,
  isMarked,
  confidentiality,
  isDayMonthField,
  setFieldValue,
  ...props
}: Props) => {
  const [textValue, setTextValue] = useState("");
  const theme = useTheme() as IStyledTheme;
  const { t } = useTranslation();
  const isDarkMode = theme.isDark;
  const taxonomy = useSelector((s) => s.taxonomy);
  const classes = getClasses(theme, isDarkMode, disabled);
  const [loading, setLoading] = useState(false);
  const styles = getTextFieldStyle(
    transparent,
    theme,
    isMarked,
    confidentiality
  );

  function handleMaskChange(value) {
    setTextValue(value);
  }

  function handleChange(event) {
    setTextValue(event.target.value);
  }

  const maskProps = useMask({
    value: textValue ? textValue?.replace("/", "") : "",
    onChange: handleMaskChange,
    mask: "##/##",
    placeholder: "DD/MM",
  });

  useEffect(() => {
    setTextValue(value);
  }, [value]);

  function prepareFormValue(key: string, value: any): any {
    switch (key) {
      case "brandCode":
        return transformTaxonomyValue(value, taxonomy, "VehicleBrand", t);
      case "carUsageCode":
        return transformTaxonomyValue(value, taxonomy, "VehicleUsageType", t);
      case "certificateNumber": //
        return value;
      case "co2Emission":
        return value?.toString();
      case "cubicCapacityCcm": //
        return value?.toString();
      case "emptyWeightKg": //
        return value?.toString();
      case "firstRegistrationDate":
      case "registrationDate":
        return adjustDateValue(value);
      case "isPluginHybrid":
        return value;
      case "maxGrossWeightKg": //
        return value?.toString();
      case "model": //
        return value;
      case "powerKw": //
        return value?.toString();
      case "seats": //
        return value?.toString();
      case "truckMountingCode":
        return transformTaxonomyValue(value, taxonomy, "TruckMounting", t);
      case "vehicleBodyTypeCode":
        return transformTaxonomyValue(value, taxonomy, "VehicleBodyType", t);
      case "vehicleClassCode":
        return transformTaxonomyValue(value, taxonomy, "VehicleClass", t);
      case "vehicleMainGroupCode":
        return transformTaxonomyValue(value, taxonomy, "VehicleMainGroup", t);
      case "vehiclePowerTypeCode":
        return transformTaxonomyValue(value, taxonomy, "VehiclePowerType", t);
      case "vehicleTypeCode":
        return transformTaxonomyValue(value, taxonomy, "VehicleTypeLegal", t);
      case "vin": //
        return value;
      case "weightCapacityKg":
        return value?.toString();
      case "yearOfConstruction":
        return value
          ? {
              value: value,
              label: value,
            }
          : null;
      default:
        return value;
    }
  }

  return (
    <FieldContainer isTooltipHidden={disabled} tooltipText={label}>
      <div
        style={{
          display: "flex",
          alignItems: "end",
          width: "100%", // Ensure the container spans the full width
        }}
      >
        <div style={{ flexGrow: 1 }}>
          <TextField
            key={value}
            id={name + "-input"}
            name={name}
            onBlur={(ev) => {
              onChange(ev);
            }}
            onRenderLabel={() => (
              <Text className={classes.label}>
                {t(label)}{" "}
                <Text className={classes.labelAsterix}>{required && "*"}</Text>
              </Text>
            )}
            styles={styles}
            disabled={disabled}
            label={t(label)}
            onChange={handleChange}
            {...(!isDayMonthField && {
              defaultValue: value ? value?.replace("/", "") : "",
            })}
            errorMessage={name && errors[name]}
            autoComplete="off"
            {...(isDayMonthField && (maskProps as any))}
            {...props}
          />
        </div>
        <IconButton
          iconProps={{ iconName: "TriggerApproval" }}
          title={t("header.Search")}
          ariaLabel={t("header.Search")}
          onClick={() => {
            setLoading(true);
            API.getVehicleByVinOrRegistrationDocumentOrTechnicalCertificateCZ(
              0,
              value
            )
              .then((res) => {
                if (res) {
                  const vehicleDataObject = res;
                  //iterate all keys of vehicleDataObject and set the value of the key to the value of the key in the object
                  Object.keys(vehicleDataObject).forEach((key) => {
                    if (key!=="vin") {
                      setFieldValue(
                        key,
                        prepareFormValue(key, vehicleDataObject[key])
                      );
                  }
                  });
                }
              })
              .finally(() => {
                setLoading(false);
              });
          }}
          disabled={disabled || loading}
          style={{
            flexShrink: 0, // Ensure the button only takes the space it needs
          }}
        />
      </div>
    </FieldContainer>
  );
};

export default VinSearchTextInputField;
